import { jsonHeaders } from "./aiTriageComposer";

const MAX_SUGGESTIONS = 4;
const MAX_SUGGESTION_LENGTH = 48;
const CARD_HOST_ID = "ai-triage-card-host";
const SUGGESTION_TRAY_ID = "ai-triage-suggestion-tray";
const CHIP_SELECTOR = ".ai-triage-chip";
export const SUGGESTION_TRANSCRIPT_LOG_ID = "messages";
export const SUGGESTION_LABELS_DATA_ATTR = "data-ai-triage-suggestion-labels";
export const SUGGESTION_CHIP_PRESSES_PATH =
  "/ai_triage/suggestion_chip_presses";

export const OPENING_CHIPS_DATA_ATTR = "data-opening-suggestion-chips";

// Starter chips are picked server-side from active AiTriageSuggestionChipPress rows.
export function readOpeningChips(root = document) {
  const raw = root
    .getElementById(SUGGESTION_TRAY_ID)
    ?.getAttribute(OPENING_CHIPS_DATA_ATTR);
  try {
    const chips = JSON.parse(raw || "[]");
    return Array.isArray(chips) ? chips.filter((c) => c?.key && c?.label) : [];
  } catch (_error) {
    return [];
  }
}

export function postSuggestionChipPress(
  chipKey,
  { fetchImpl = fetch, token = "" } = {}
) {
  return fetchImpl(SUGGESTION_CHIP_PRESSES_PATH, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ chip_key: chipKey }),
    keepalive: true,
  }).catch(() => {});
}

export const SUGGESTION_CHIP_CLASS =
  "ai-triage-chip inline-flex items-center justify-center px-3 py-1.5 rounded-full border border-neutral-200 bg-white text-sm leading-tight text-neutral-700 shadow-sm transition-colors hover:enabled:border-green-lighter hover:enabled:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-lighter focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

export const AI_TRIAGE_SUGGEST_EVENT = "ai-triage:suggest";
export const AI_TRIAGE_SUGGESTIONS_EVENT = "ai-triage:suggestions";
export const AI_TRIAGE_RECOMMENDATION_EVENT = "ai-triage:recommendation";

export function notifySuggestionLabels(labels = []) {
  window.dispatchEvent(
    new CustomEvent(AI_TRIAGE_SUGGESTIONS_EVENT, {
      detail: { labels },
    })
  );
}

export function notifyAiTriageRecommendation(detail = {}) {
  window.dispatchEvent(
    new CustomEvent(AI_TRIAGE_RECOMMENDATION_EVENT, {
      detail,
    })
  );
}

function replyInFlightFromHost(root = document) {
  const host =
    root.getElementById?.(CARD_HOST_ID) ||
    root.querySelector?.(`#${CARD_HOST_ID}`);
  return host?.dataset?.replyInFlight === "true";
}

export function visibleSuggestionLabels(suggestions) {
  return (suggestions || [])
    .map((chip) => chip.toString().trim())
    .filter((chip) => chip.length > 0 && chip.length <= MAX_SUGGESTION_LENGTH)
    .slice(0, MAX_SUGGESTIONS);
}

export function trayChipLabels({
  hasMessages = false,
  replyInFlight = false,
  followUpLabels = [],
  openingLabels = [],
} = {}) {
  if (replyInFlight) {
    return [];
  }
  if (!hasMessages) {
    return openingLabels;
  }
  return visibleSuggestionLabels(followUpLabels);
}

export function writeSuggestionLabelsOnMessage(element, suggestions) {
  const labels = visibleSuggestionLabels(suggestions);
  if (!element) {
    return labels;
  }
  element.setAttribute(SUGGESTION_LABELS_DATA_ATTR, JSON.stringify(labels));
  return labels;
}

export function latestFollowUpSuggestionLabels(root = document) {
  const log =
    root.getElementById?.(SUGGESTION_TRANSCRIPT_LOG_ID) ||
    root.querySelector?.(`#${SUGGESTION_TRANSCRIPT_LOG_ID}`);
  if (!log) {
    return [];
  }

  const nodes = log.querySelectorAll(`[${SUGGESTION_LABELS_DATA_ATTR}]`);
  if (nodes.length === 0) {
    return [];
  }

  const raw = nodes[nodes.length - 1].getAttribute(SUGGESTION_LABELS_DATA_ATTR);
  try {
    return visibleSuggestionLabels(JSON.parse(raw || "[]"));
  } catch (_error) {
    return [];
  }
}

export function retireStaleSuggestionChips(root = document) {
  const tray =
    root.getElementById?.(SUGGESTION_TRAY_ID) ||
    root.querySelector(`#${SUGGESTION_TRAY_ID}`);
  if (!tray) {
    return;
  }

  const replyInFlight = replyInFlightFromHost(root);
  tray.querySelectorAll(CHIP_SELECTOR).forEach((button) => {
    button.disabled = replyInFlight;
  });
}
